import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent, Tooltip } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getEventStatusFilteringOptions,
  getClearEventStatusModel,
  getNotificationTypes,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import {
  generateNoDataValues,
  getFiltersInfo,
  getOperationalStatusNameFromId,
  getOperationalStatusStyleName,
  validateEmailFormat,
} from "../../common/utils";
import TrueFalseImage from "../../components/TrueFalseImage";
import AddOrEditEventStatus from "./components/AddOrEditEventStatus";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function EventStatus() {
  const {
    statusModel,
    setStatusModel,
    statusData,
    STATUS_ENDPOINT,
    config,
    pageIndexExecutionStatus,
    setPageIndexExecutionStatus,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [dataFiltered, setDataFiltered] = useState(statusData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getEventStatusFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const statusDataObj = statusData.Data.find(
        (workflowObj) => workflowObj.Id === Number(id)
      );
      setStatusModel(statusDataObj);
    }
  }, [id]);

  useEffect(() => {
    setDataFiltered(statusData.Data);
  }, [statusData]);

  const validateOperationResult = (result, isDelete) => {
    setStatusModel(getClearEventStatusModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([STATUS_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexExecutionStatus !== 0 && statusModel.Data.length === 1) {
        setPageIndexExecutionStatus(pageIndexExecutionStatus - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${STATUS_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${STATUS_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${STATUS_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () => {
    let preResult = false;
    if (!!statusModel?.notifyByEmail) {
      preResult =
        statusModel?.notificationType <= 0 ||
        !statusModel?.emails ||
        statusModel?.emails.replaceAll(" ", "").length <= 0 ||
        !validateEmailFormat(statusModel?.emails?.split(";"));
    }

    return (
      preResult ||
      !statusModel?.Name ||
      statusModel?.Name.replaceAll(" ", "").length <= 0 ||
      statusModel?.OperationalStatus <= 0
    );
  };

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.background_color,
      localize.foreground_color,
      localize.operational_status,
      localize.is_initial_status,
      localize.is_final_status,
      localize.notification,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.execution_statuses}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={STATUS_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="event_status"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={`${localize.edit} ${localize.execution_status}`}
          onClickToSubmit={async () => {
            await handleUpdateRequest(statusModel);
            setShowEditModal(false);
            setStatusModel(getClearEventStatusModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setStatusModel(getClearEventStatusModel());
          }}
          Component={
            <AddOrEditEventStatus
              model={statusModel}
              setModel={setStatusModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.execution_statuses}
            subtitle={localize.execution_statuses_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="event_status_filter"
            handleAddBtn={() => {
              setStatusModel(getClearEventStatusModel());
              setShowAddModal(true);
            }}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_execution_status}
                onClickToSubmit={async () => {
                  await handleCreateRequest(statusModel);
                  setShowAddModal(false);
                  setStatusModel(getClearEventStatusModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setStatusModel(getClearEventStatusModel());
                }}
                Component={
                  <AddOrEditEventStatus
                    model={statusModel}
                    setModel={setStatusModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      <DeleteConfirmationMessage
        entityName={statusModel.Name}
        entityType={localize.status}
        onClickForCancel={() => {
          toggleVisibleModalConfirm();
          setStatusModel(getClearEventStatusModel());
        }}
        onClickForConfirm={() => {
          handleDeleteRequest(statusModel.Id);
        }}
      />

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexExecutionStatus}
            setPageIndex={setPageIndexExecutionStatus}
            dataArray={statusData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          statusData.Data?.length !== 0
            ? dataFiltered.map((item) => ({
                key: item.key,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setStatusModel({
                          ...item,
                          notificationTypeSelected:
                            getNotificationTypes().filter(
                              (x) => x.Id === item.notificationType
                            )[0],
                          operationalStatusModel: {
                            Id: item.OperationalStatus,
                            Name: getOperationalStatusNameFromId(
                              item.OperationalStatus
                            ),
                          },
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setStatusModel({
                          ...item,
                          operationalStatusModel: {
                            Id: item.OperationalStatus,
                            Name: getOperationalStatusNameFromId(
                              item.OperationalStatus
                            ),
                          },
                        });
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <Tooltip title={item.BackgroundColor}>
                    <button
                      className="btn btn-sm background_color_representation"
                      style={{ backgroundColor: `${item.BackgroundColor}` }}
                    />
                  </Tooltip>,
                  <Tooltip title={item.ForegroundColor}>
                    <p
                      className="text-2xl foreground_color_representation"
                      style={{ color: `${item.ForegroundColor}` }}
                    >
                      Aa
                    </p>
                  </Tooltip>,
                  <p
                    className={`rounded p-2 ${getOperationalStatusStyleName(
                      item.OperationalStatus
                    )}`}
                  >
                    {getOperationalStatusNameFromId(item.OperationalStatus)}
                  </p>,
                  <TrueFalseImage value={item.IsInitial} />,
                  <TrueFalseImage value={item.IsFinal} />,
                  <TrueFalseImage
                    value={item.notifyByEmail || item.createNotification}
                  />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(10)]
        }
      />
    </div>
  );
}
